import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import 'katex/dist/katex.min.css';

// Состояния и API
import { formulasState, isLoadingState, qrModalState } from '../../../atoms/dataAtoms';
import { physicsApi } from '../../../api';
import { useAuth } from '../../../hooks/useAuth';

// UI Компоненты
import FormulaLoading from '../formuls/FormulaLoading';
import FormulaNothingFound from '../formuls/FormulaNothingFound';
import SEO from '../../../seo';
import CalculatorActivity from './CalculatorActivity';
import QrModal from '../../components/qrModal/QrModal'; // Твоя новая модалка

// Категории и иконки
import { ReactComponent as IconMechanics } from '../../../assets/images/formula_icon0.svg';
import { ReactComponent as IconThermo } from '../../../assets/images/formula_icon1.svg';
import { ReactComponent as IconOptics } from '../../../assets/images/formula_icon2.svg';
import { ReactComponent as IconNuclear } from '../../../assets/images/formula_icon3.svg';
import { ReactComponent as IconElectric } from '../../../assets/images/formula_icon4.svg';
import { ReactComponent as IconMolecular } from '../../../assets/images/formula_icon5.svg';
import { ReactComponent as IconAtomic } from '../../../assets/images/formula_icon6.svg';
import { ReactComponent as IconConstants } from '../../../assets/images/formula_icon7.svg';
import { CATEGORIES } from '../../utils/mappings';

const CATEGORY_ICONS = {
    0: IconMechanics, 1: IconThermo, 2: IconOptics, 3: IconNuclear,
    4: IconElectric, 5: IconMolecular, 6: IconAtomic, 7: IconConstants
};

const CalculatorsFragment = () => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language ? i18n.language.substring(0, 2) : 'en';
    const history = useHistory();
    const { calcId } = useParams();

    // Авторизация и состояние QR
    const { isAuthenticated } = useAuth();
    const [isQrOpen, setIsQrOpen] = useRecoilState(qrModalState);

    const [formulas, setFormulas] = useRecoilState(formulasState);
    const [loading, setLoading] = useRecoilState(isLoadingState);

    const [searchTerm, setSearchTerm] = useState('');
    const [placeholderText, setPlaceholderText] = useState(t('translate.calc_name'));

    // 1. Загрузка данных
    useEffect(() => {
        const fetchData = async () => {
            if (formulas.length > 0) return;
            setLoading(true);
            try {
                const response = await physicsApi.getFormulaList(currentLang);
                setFormulas(response.data);
            } catch (error) {
                console.error("Ошибка загрузки:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [setFormulas, formulas.length, setLoading, currentLang]);

    // 2. Рандомный плейсхолдер для поиска
    useEffect(() => {
        const calcFormulas = formulas.filter(item => item.calculator && item.calculator.variablesMap);
        if (calcFormulas.length > 0) {
            const randomIndex = Math.floor(Math.random() * calcFormulas.length);
            const randomFormula = calcFormulas[randomIndex];
            if (randomFormula?.content?.formulaName) {
                setPlaceholderText(`${randomFormula.content.formulaName}...`);
            }
        }
    }, [formulas]);

    // 3. Если зашли по прямой ссылке без авторизации — сразу показываем QR
    useEffect(() => {
        if (calcId && !isAuthenticated) {
            setIsQrOpen(true);
        }
    }, [calcId, isAuthenticated, setIsQrOpen]);

    // Фильтрация
    const filteredCalculators = useMemo(() => {
        let filtered = formulas.filter(item => item.calculator && item.calculator.variablesMap);
        if (searchTerm) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.content.formulaName.toLowerCase().includes(query)
            );
        }
        return filtered.sort((a, b) => a.content.formulaName.localeCompare(b.content.formulaName, currentLang));
    }, [formulas, searchTerm, currentLang]);

    // Активный калькулятор из URL
    const activeCalc = useMemo(() => {
        if (!calcId || formulas.length === 0) return null;
        return formulas.find(f => String(f._id) === String(calcId));
    }, [formulas, calcId]);

    // ОБРАБОТКА КЛИКА: Проверка доступа
    const handleCalcClick = (item) => {
        if (isAuthenticated) {
            history.push(`/calcs/${item._id}`);
        } else {
            setIsQrOpen(true); // Открываем QR модалку
        }
    };

    const handleCloseModal = () => {
        history.push('/calcs');
    };

    if (loading) return <FormulaLoading />;

    return (
        <div className="formulas-page">
            <SEO>
                <title>{`${t('translate.calc_name')}: ${t('translate.app_name2')}`}</title>
            </SEO>

            {/* --- TOOLBAR --- */}
            <div className="toolbar">
                <div className="toolbar__header">
                    <h1 className="toolbar__title">{t('translate.app_name2')}</h1>
                    <span className="toolbar__subtitle_formuls">{t('translate.calc_name')}</span>
                </div>

                <div className="toolbar__search-wrapper">
                    <input
                        type="text"
                        className="toolbar__input"
                        placeholder={placeholderText}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className='toolbar__reset-button' onClick={() => setSearchTerm('')} />
                    )}
                </div>
            </div>

            {/* --- ГРИД КАРТОЧЕК --- */}
            {filteredCalculators.length > 0 ? (
                <div className="formulas-grid">
                    {filteredCalculators.map((item) => {
                        const categoryId = item.category !== undefined ? item.category : 0;
                        const IconComponent = CATEGORY_ICONS[categoryId] || CATEGORY_ICONS[0];
                        const categoryTitle = CATEGORIES[categoryId] ? t(CATEGORIES[categoryId].titleKey) : '';

                        return (
                            <div
                                key={item._id}
                                className={`calc-card calc-card--category-${categoryId}`}
                                onClick={() => handleCalcClick(item)}
                            >
                                <div className="calc-card__icon"><IconComponent /></div>
                                <div className="calc-card__content">
                                    <h3 className="calc-card__title" title={item.content.formulaName}>
                                        {item.content.formulaName}
                                    </h3>
                                    <p className="calc-card__desc">{item.content.description}</p>
                                    <div className="calc-card__badge">{categoryTitle}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <FormulaNothingFound searchTerm={searchTerm} />
            )}

            {/* МОДАЛКА КАЛЬКУЛЯТОРА (только при наличии авторизации) */}
            {activeCalc && isAuthenticated && (
                <CalculatorActivity
                    formula={activeCalc}
                    colorId={activeCalc.color}
                    onClose={handleCloseModal}
                />
            )}

            {/* МОДАЛКА QR (если доступ закрыт) */}
            {isQrOpen && (
                <QrModal onClick={() => setIsQrOpen(false)} />
            )}

            <div className="settings-footer" style={{marginTop: '40px'}}>
                <p>© <a href='https://chernykh.tech/' target='_blank' rel='noreferrer'>chernykh.tech</a> / All rights reserved, {new Date().getFullYear()}</p>
            </div>
        </div>
    );
};

export default CalculatorsFragment;